import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { api } from "../../../api/operationsApi";
import LoadingFailedAlert from "../../common/LoadingFailedAlert";
import PageTitle from "../../common/PageTitle";
import DwhSyncStartResults from "./DwhSyncStartResults";
import type { DwhSyncStartResult } from "./dwhSyncStartResult";

const startSync = withErrorHandling(api.dwhSync.startSync);

const DwhFullSyncContent = () => {
  const [selectedClientCodes, setSelectedClientCodes] = useState<string[]>([]);
  const [syncBc, setSyncBc] = useState(true);
  const [syncCore, setSyncCore] = useState(true);
  const [recreateSchema, setRecreateSchema] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [currentClientCode, setCurrentClientCode] = useState<string>();
  const [results, setResults] = useState<DwhSyncStartResult[]>([]);
  const [clients, fetchError] = useFetch(api.dwhSync.getSyncClients);

  const isProcessing = currentClientCode !== undefined;
  const hasSource = syncBc || syncCore;
  const canStart = selectedClientCodes.length > 0 && hasSource && !isProcessing;

  if (fetchError) {
    logError(fetchError, "[DwhFullSyncContent] getSyncClients");
    return <LoadingFailedAlert apiError={fetchError} />;
  }

  if (clients === undefined) {
    return <InlineLoader />;
  }

  const handleExecute = async () => {
    if (!canStart) {
      return;
    }

    setConfirmationOpen(false);
    setResults([]);

    for (const clientCode of selectedClientCodes) {
      setCurrentClientCode(clientCode);
      const [response, error] = await startSync({
        clientCode,
        startExportFromBc: syncBc,
        importDataFromCore: syncCore,
        recreateDatabaseSchema: recreateSchema,
      });

      if (error) {
        logError(error, `[DwhFullSyncContent] startSync ${clientCode}`);
        setResults((previous) => [...previous, { clientCode, status: "error", errorMessage: error.message }]);
        continue;
      }

      setResults((previous) => [
        ...previous,
        {
          clientCode,
          status: response.previousSyncInProgress ? "already-running" : "started",
          correlationId: response.correlationId ?? undefined,
        },
      ]);
    }

    setCurrentClientCode(undefined);
  };

  const handleStartClick = () => {
    if (recreateSchema) {
      setConfirmationOpen(true);
      return;
    }

    void handleExecute();
  };

  return (
    <Stack spacing={3} sx={{ alignItems: "flex-start", width: "100%", maxWidth: "48rem" }}>
      <PageTitle title="DWH Sync" subtitle="Sync data from Business Central and Core">
        Select clients with DWH enabled and choose the data sources to synchronize. Start requests are submitted one
        client at a time. An accepted request does not mean the sync has completed. Existing auto-sync settings are
        preserved.
      </PageTitle>
      <Autocomplete
        multiple
        fullWidth
        disableCloseOnSelect
        options={clients}
        value={selectedClientCodes}
        disabled={isProcessing}
        onChange={(_, value) => setSelectedClientCodes(value)}
        renderInput={(params) => <TextField {...params} label="Clients" placeholder="Select client codes" />}
      />
      {clients.length === 0 && <Alert severity="info">No clients have DWH enabled.</Alert>}
      <FormControl component="fieldset" disabled={isProcessing} error={!hasSource}>
        <FormLabel component="legend">Sync sources</FormLabel>
        <FormGroup>
          <FormControlLabel
            label="Sync BC (Business Central)"
            control={
              <Checkbox
                checked={syncBc}
                onChange={(_, checked) => {
                  setSyncBc(checked);
                  if (!checked) {
                    setRecreateSchema(false);
                  }
                }}
              />
            }
          />
          <FormControlLabel
            label="Sync Core"
            control={
              <Checkbox
                checked={syncCore}
                onChange={(_, checked) => {
                  setSyncCore(checked);
                  if (!checked) {
                    setRecreateSchema(false);
                  }
                }}
              />
            }
          />
        </FormGroup>
        {!hasSource && <FormHelperText>Select at least one sync source.</FormHelperText>}
      </FormControl>
      <FormControl>
        <FormControlLabel
          label="Recreate database schema"
          disabled={isProcessing || !syncBc || !syncCore}
          control={<Checkbox checked={recreateSchema} onChange={(_, checked) => setRecreateSchema(checked)} />}
        />
        <FormHelperText>Rebuilds the DWH schema and reloads all data. Requires both BC and Core sync.</FormHelperText>
      </FormControl>
      {recreateSchema && (
        <Alert severity="warning">
          Existing DWH schemas will be dropped and recreated for every selected client. DWH data will be unavailable
          until the rebuild completes.
        </Alert>
      )}
      <Button variant="contained" onClick={handleStartClick} disabled={!canStart} loading={isProcessing}>
        Start DWH Sync
      </Button>
      {isProcessing && (
        <Typography role="status">
          Submitting {currentClientCode} ({results.length + 1} of {selectedClientCodes.length})…
        </Typography>
      )}
      <DwhSyncStartResults results={results} />
      <SmallDialog open={confirmationOpen} onClose={() => setConfirmationOpen(false)}>
        <DialogTitle>Recreate DWH schemas?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Drop and recreate the existing DWH schemas for {selectedClientCodes.join(", ")}? All DWH data will be
            reloaded from BC and Core. DWH data will be unavailable until the rebuild completes.
          </DialogContentText>
        </DialogContent>
        <DialogButtons onCancel={() => setConfirmationOpen(false)}>
          <Button variant="contained" color="error" onClick={handleExecute}>
            Recreate and Sync
          </Button>
        </DialogButtons>
      </SmallDialog>
    </Stack>
  );
};

export default DwhFullSyncContent;
